<template>
  <div class="container-fluid px-0">
    <div class="row mb-4 g-2">
      <!-- Class Filter -->
      <div class="col-6 col-md-2 col-lg-2">
        <select
          class="form-select form-select-sm"
          v-model="selectedClass"
          @change="handleClassFilter"
        >
          <option value="">{{ t('filters.allClasses') }}</option>
          <option
            v-for="classItem in classes"
            :key="classItem.id"
            :value="classItem.id"
          >
            {{ classItem.name }}
          </option>
        </select>
      </div>

      <!-- Subject Filter -->
      <div class="col-6 col-md-2 col-lg-2">
        <select
          class="form-select form-select-sm"
          v-model="selectedSubject"
          @change="handleSubjectFilter"
        >
          <option value="">{{ t('filters.allSubjects') }}</option>
          <option
            v-for="subject in subjects"
            :key="subject.id"
            :value="subject.id"
          >
            {{ subject.name }}
          </option>
        </select>
      </div>

      <!-- Teacher Filter -->
      <div class="col-6 col-md-2 col-lg-2">
        <select
          class="form-select form-select-sm"
          v-model="selectedTeacher"
          @change="handleTeacherFilter"
        >
          <option value="">{{ t('filters.allTeachers') }}</option>
          <option
            v-for="teacher in teachers"
            :key="teacher.id"
            :value="teacher.id"
          >
            {{ teacher.username }}
          </option>
        </select>
      </div>

      <!-- Reset Filter button (desktop) -->
      <div class="col-md-1 d-none d-md-block">
        <button 
          class="btn btn-outline-secondary btn-sm w-100" 
          @click="resetFilters"
          :title="t('filters.reset')"
        >
          <i class="fas fa-filter-circle-xmark"></i>
        </button>
      </div>  

      <!-- Search input -->
      <div class="col-6 col-md-3 col-lg-3 position-relative">
        <input
          type="text"
          class="form-control form-control-sm ps-5"
          :placeholder="t('filters.searchPlaceholder')"
          v-model="searchTerm"
          @input="handleSearch"
          style="padding-right: 12px"
        />
        <span class="position-absolute start-0 top-50 translate-middle-y ps-3">
          <i class="fas fa-search"></i>
        </span>
      </div>

      <!-- Reset Filter button (mobile) -->
      <div class="col-12 d-md-none">
        <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
          <i class="fas fa-filter-circle-xmark"></i> {{ t('filters.reset') }}
        </button>
      </div>

      <!-- Add Assignment Button -->
      <div class="col-12 col-md-2 col-lg-2">
        <button 
          class="btn btn-secondary btn-sm w-100" 
          @click="$emit('add')"
        >
          <i class="fas fa-plus me-2"></i>
          {{ t('filters.add') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, defineEmits } from "vue";
import { debounce } from "lodash";
import api from "@/api";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const searchTerm = ref("");
const selectedClass = ref("");
const selectedGroup = ref("");
const selectedSubject = ref("");
const selectedTeacher = ref("");
const classes = ref([]);
const groups = ref([]);
const subjects = ref([]);
const teachers = ref([]);

const emit = defineEmits([
  "search", 
  "add", 
  "filter-by-class",
  "filter-by-group",
  "filter-by-subject", 
  "filter-by-teacher", 
  "reset-filters"
]);

onMounted(async () => {
  try {
    const [classesRes, groupsRes, subjectsRes, teachersRes] = await Promise.all([
      api.get("/classes"),
      api.get("/groups"),
      api.get("/subjects"),
      api.get("/teachers")
    ]);
    classes.value = classesRes.data.data;
    groups.value = groupsRes.data.data;
    subjects.value = subjectsRes.data.data;
    teachers.value = teachersRes.data.data;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
});

const handleSearch = debounce(() => {
  emit("search", searchTerm.value);
}, 500);

const handleClassFilter = () => {
  selectedGroup.value = "";
  emit("filter-by-class", selectedClass.value);
};

const handleSubjectFilter = () => {
  emit("filter-by-subject", selectedSubject.value);
};

const handleTeacherFilter = () => {
  emit("filter-by-teacher", selectedTeacher.value);
};

const resetFilters = () => {
  handleSearch.cancel();
  
  searchTerm.value = "";
  selectedClass.value = "";
  selectedGroup.value = "";
  selectedSubject.value = "";
  selectedTeacher.value = "";
  
  emit("search", "");
  emit("filter-by-class", "");
  emit("filter-by-group", "");
  emit("filter-by-subject", "");
  emit("filter-by-teacher", "");
  emit("reset-filters");
};
</script>
